/**
 * La roue du jour au bout d'un exercice (migration 0741) : un exercice terminé donne droit à UN tour par
 * jour. Le bouton n'apparaît que si le tour est permis (`can_spin`) ; il ouvre la même roue que la
 * collection. Rien pour une session anonyme ni en aperçu (l'appelant décide avec `show`).
 */
import { useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useAuth } from '@/lib/auth';
import {
  goldSlices,
  myTrousseKey,
  spinTrousseWheel,
  trousseCollectionKey,
  trousseWheelKey,
  useEquipFromCollection,
  useTrousseWheelState,
  winChanceLabel,
  WHEEL_SLICES,
} from '@/lib/trousses';
import { WheelDialog, WheelDisc } from './wheel';

/** Ajoute le bouton de la roue en tête du pied du bilan, quand `show` est vrai. */
export function withTrousseWheel(show: boolean, footer: ReactNode): ReactNode {
  if (!show) return footer;
  return (
    <div className="space-y-3">
      <TrousseWheelEndButton />
      {footer}
    </div>
  );
}

export function TrousseWheelEndButton() {
  const { user } = useAuth();
  const userId = user && !user.is_anonymous ? user.id : '';
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data: spin } = useTrousseWheelState(userId || null);
  const equip = useEquipFromCollection(userId);
  const [open, setOpen] = useState(false);
  // le bouton reste affiché le temps de voir le résultat, même une fois la roue tournée
  const [spun, setSpun] = useState(false);

  if (!userId || !spin || (!spin.can_spin && !spun)) return null;
  const gold = goldSlices(spin.chance);

  return (
    <>
      <button
        type="button"
        data-testid="wheel-end-button"
        disabled={spun && !open}
        onClick={() => setOpen(true)}
        className="flex w-full items-center gap-3 rounded-2xl border-2 border-[#f5b82e] bg-[#061433] p-3 text-left text-white active:scale-[0.98] disabled:opacity-70"
      >
        <span className={spun ? '' : 'animate-[spin_12s_linear_infinite]'}>
          <WheelDisc size={48} gold={gold} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="font-display block text-base font-extrabold">
            {spun ? 'Roue tournée pour aujourd’hui' : 'Tourne la roue du jour !'}
          </span>
          <span className="block text-sm text-[#f5d27a]">
            {spun
              ? 'Reviens demain après un exercice.'
              : `${winChanceLabel(spin.chance)} de gagner une trousse (${gold}/${WHEEL_SLICES})`}
          </span>
        </span>
      </button>

      {open && (
        <WheelDialog
          winChance={spin.chance}
          onSpin={async () => {
            const r = await spinTrousseWheel();
            setSpun(true);
            void qc.invalidateQueries({ queryKey: trousseWheelKey(userId) });
            void qc.invalidateQueries({ queryKey: trousseCollectionKey(userId) });
            void qc.invalidateQueries({ queryKey: myTrousseKey(userId) });
            return r;
          }}
          onClose={() => setOpen(false)}
          onEquip={(slug) => {
            equip.mutate(slug);
            setOpen(false);
          }}
          onCollection={() => {
            setOpen(false);
            void navigate({ to: '/trousses' });
          }}
        />
      )}
    </>
  );
}
